import {
    NetworkError,
    generateRouteHandler,
    makeCanonicalUrl,
    permuteUrlRules,
    shouldRenderContextualMenu,
} from '@jet-app/music-classical/utilities';
import {
    DETAIL_PAGE_TYPE_ALLOWLIST,
    type DetailPageIntent,
    makeDetailPageIntent,
    DetailPageType,
    DETAIL_PAGE_TYPE_ALLOWLIST_SUPER_ROOMS,
} from './DetailPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { DetailPage } from '@jet-app/music-classical/models/pages/detail';
import { IntentKind } from '@jet-app/music-classical/intents';
import { renderDetailPage } from './renderDetailPage';
import { requestViewPage } from '@jet-app/music-classical/api/request-view-page';
import { renderSeeAllPage } from '../see-all/renderSeeAllPage';
import { requestEntitiesList } from '@jet-app/music-classical/api/request-entities-list';

const canonicalUrlRule = {
    path: '/{storefront}/{pageType}/{id}',
    query: [],
};

export const DetailPageIntentController: IntentController<DetailPageIntent> &
    RouteProvider = {
    $intentKind: IntentKind.DETAIL_PAGE,

    routes: function (objectGraph: ObjectGraph) {
        return [
            {
                rules: permuteUrlRules(canonicalUrlRule),
                handler: (url, handlerParams) => {
                    const pageType = handlerParams.pageType as DetailPageType;
                    if (DETAIL_PAGE_TYPE_ALLOWLIST.includes(pageType)) {
                        return generateRouteHandler(
                            makeDetailPageIntent,
                            objectGraph,
                        ).apply(this, [url, handlerParams]);
                    }
                },
            },
        ];
    },

    async perform(
        intent: DetailPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<DetailPage> {
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        const { entityPath } = intent;
        let apiResponse;

        if (entityPath) {
            apiResponse = await requestEntitiesList(entityPath, objectGraph);
        } else {
            apiResponse = await requestViewPage(
                `${intent.pageType}/${intent.id}`,
                objectGraph,
            );
        }

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        const contextualMenuResponse = await shouldRenderContextualMenu(
            apiResponse.body.userComponentsAction?.url,
            objectGraph,
        );

        const page =
            intent.pageType === 'charts'
                ? renderSeeAllPage(apiResponse, objectGraph, canonicalURL, true)
                : renderDetailPage(
                      canonicalURL,
                      apiResponse,
                      contextualMenuResponse,
                      objectGraph,
                      intent.id,
                  );

        return page;
    },
};
